import { TemplateMessageObject } from "@trendbuild/trend-cloud-api";
import { Pager } from "../commons/pager";
import { Base } from "./base.model";
import { Campaign } from "./campaign.model";
import { Card } from "./card.model";
import { Channel, isHybridChannel } from "./channel.model";
import { Company } from "./company.model";
import { Contact } from "./contact.model";
import { Department } from "./department.model";
import { AttendanceStatusEnum } from "./enum/attendance.status.enum";
import { ChannelTypeEnum } from "./enum/channel.type";
import { Message } from "./message.model";
import { SalesMonitor } from "./sales-monitor.model";
import { Tabulation } from "./tabulation.model";
import { Tag } from "./tag.model";
import { User } from "./user.model";
import { WaTemplateParams } from "./wa-template-definition.model";

export interface Attendance extends Base {
  protocol: string;
  contact: Contact;
  endDate: Date;
  company: Company;
  channel: Channel;
  user: User;
  lastMessage: Message;
  department: Department;
  campaign?: Campaign; // originating campaign when opened by a campaign response
  status: AttendanceStatusEnum;
  card: Card;
  tags: Tag[];
  inConversation: boolean;
  expiredAt: Date;
  countUnreadMessages: number;
  chatbotStatus: ChatbotStatus;
  metadata: MetadataAttedance;
  kpis: KpisTypes;
  closedAt: Date;
  feedbackCSAT: number;
  isDirectedByCustomerPortfolio: boolean;
  tabulationStatus: TabulationStatus;
  salesMonitorStatus: SalesMonitorStatus;
  triggerStatus?: TriggerStatus;
}

export interface ChatbotStatus {
  navPath: string;
  automated: boolean;
  running: boolean;
  enabled: boolean;
  expiredAt: Date;
};

export interface TriggerStatus {
  actived: boolean;
  name: string;
  keys: string[];
  message: string;
}

export interface TabulationStatus {
  description: string;
  tabulations: Array<Tabulation>;
}

export interface SalesMonitorStatus {
  budget?: number;
  closedAt: Date;
  salesMonitor: SalesMonitor;
}

export interface KpisTypes {
  tmfh: number;
  tme: number;
  tma: number;
  tmp: number;
  tmta: number;
};

export interface CreateAttendanceFromTemplate {
  idDepartment: string;
  idChannel: string;
  idContact: string;
  template: TemplateMessageObject;
  /** Array for POSITIONAL templates, object keyed by placeholder name for NAMED ones. */
  headerParams: WaTemplateParams;
  bodyParams: WaTemplateParams;
  previewText: string;
}

export interface Attendances {
  counts: Array<CountState>,
  pager: Pager<Attendance>
}

export interface CountState {
  status: AttendanceStatusEnum;
  count: number;
  unread?: number; // absent on an API without the unread figure: the tab shows no badge
}

/** Web twin of AttendanceAggregation.UNREAD_WEIGHT, for the local adjustment only (SPEC-2026-10-attendance-tabs-unread-messages RF-11, RF-13). */
export const unreadWeight = (count?: number | null): number => count === -1 ? 1 : (count > 0 ? count : 0);

export interface MarkAsUnreadResponse {
  data: { idAttendance: string; countUnreadMessages: number };
  meta: Record<string, unknown>;
  error: unknown;
}

export interface MetadataAttedance {
  integrations?: AttendanceIntegration;
  webchat?: AttendanceWebchat;
}

// Webchat visitor context (SPEC-2026-07-webchat-channel, RF-12/RF-17)
export interface AttendanceWebchat {
  visitorId: string;
  startUrl?: string;
}

export interface AttendanceIntegration {
  ai?: {
    name?: string;
    actived?: boolean;
    integration?: string;
    started?: boolean;
    initialMessage: string;
    openai?: {
      /** @deprecated legacy Assistants API thread id; kept for history only. */
      threadId?: string;
      /** Responses API conversation id (`conv_…`). */
      conversationId?: string;
      assistant?: string;
    },
    gemini?: {

    },
    gpt_maker?: {
      assistant?: string;
    },
  }
}



export const CHANNEL_ICONS: Record<string, string> = {
  [ChannelTypeEnum.CLOUD_API]: '/assets/images/v2/svg/icon-whatsapp-verified.svg',
  [ChannelTypeEnum.Z_API]: '/assets/images/v2/svg/icon-whatsapp.svg',
  [ChannelTypeEnum.INSTAGRAM_API]: '/assets/images/icon-instagram.png',
  [ChannelTypeEnum.MESSENGER_API]: '/assets/images/icon-messenger.png',
  [ChannelTypeEnum.WEBCHAT_API]: '/assets/images/v2/svg/icon-webchat.svg',
  [ChannelTypeEnum.RCS_API]: '/assets/images/Google-Messages.png',
};

/** The icon of a Hybrid ("Split") channel: the official icon with its seal in the platform primary color. */
export const CHANNEL_ICON_HYBRID = '/assets/images/v2/svg/icon-whatsapp-split.svg';

/** The icon that stands for a channel on lists and cards — a Hybrid channel carries its own seal. */
export const channelIconOf = (channel?: Pick<Channel, 'type' | 'hybrid'> | null): string =>
  isHybridChannel(channel) ? CHANNEL_ICON_HYBRID : (CHANNEL_ICONS[channel?.type] ?? '');

export const STATUS_LABELS: Record<AttendanceStatusEnum, string> = {
  [AttendanceStatusEnum.OVERTIME]: 'attendance.status.overtime',
  [AttendanceStatusEnum.PENDING]: 'attendance.status.pending',
  [AttendanceStatusEnum.IN_PROGRESS]: 'attendance.status.inProgress',
  [AttendanceStatusEnum.PAUSED]: 'attendance.status.paused',
  [AttendanceStatusEnum.CLOSED]: 'attendance.status.closed',
};

export const STATUS_ICONS: Record<AttendanceStatusEnum, string> = {
  [AttendanceStatusEnum.OVERTIME]: 'mdi mdi-alert',
  [AttendanceStatusEnum.PENDING]: 'mdi mdi-clock-outline',
  [AttendanceStatusEnum.IN_PROGRESS]: 'mdi mdi-message-processing-outline',
  [AttendanceStatusEnum.PAUSED]: 'mdi mdi-pause-circle-outline',
  [AttendanceStatusEnum.CLOSED]: 'mdi mdi-check-circle-outline',
};

export const MESSAGE_TYPE_LABELS: Record<string, string> = {
  audio: 'attendance.messageType.audio',
  image: 'attendance.messageType.image',
  video: 'attendance.messageType.video',
  document: 'attendance.messageType.document',
  sticker: 'attendance.messageType.sticker',
  contacts: 'attendance.messageType.contacts',
};

export type ViewMode = 'user' | 'department';

export interface MonitoringColumn {
  type: 'status' | 'user' | 'department';
  key: string;
  label: string;
  icon?: string;
  colorClass?: string;
  filterParams?: Record<string, string>;
  channelName?: string
}

export const MONITORING_DELAY_THRESHOLD_SECONDS = 300;

export interface ColumnConfig {
  status?: AttendanceStatusEnum;
  label: string;
  icon: string;
  colorClass: string;
}

export const STATUS_COLUMNS_CONFIG: ColumnConfig[] = [
  { status: AttendanceStatusEnum.OVERTIME, label: 'Fora de Horário', icon: 'ri-alert-line', colorClass: 'bg-danger' },
  { status: AttendanceStatusEnum.PENDING, label: 'Aguardando', icon: 'ri-time-line', colorClass: 'bg-warning' },
];


